import React, { useEffect, useRef, useState } from "react";

import { useAutoAnimate } from "@formkit/auto-animate/react";
import { t } from "@lingui/core/macro";
import { Plural, Trans } from "@lingui/react/macro";
import { isEmpty, partition } from "lodash/fp";
import { DateTime } from "luxon";

import Typography from "@mui/material/Typography";

import InfoPanel from "@readai/common/components/InfoPanel";
import LoadSpinner from "@readai/common/components/LoadSpinner";
import TimezoneSelector from "@readai/common/components/TimezoneSelector";
import TooltipIcon from "@readai/common/components/TooltipIcon";
import useWindowDimensions from "@readai/common/hooks/useWindowDimensions";
import Stack, { Row } from "@readai/pulse/components/Stack";

import CalendarView from "@app/booking/components/CalendarView";
import TimeSlot from "@app/booking/components/TimeSlot";
import useAvailability from "@app/booking/hooks/useAvailability";
import { IndicatorLegend } from "@app/booking/ScheduleEvent/styles";
import useSchedulingStore from "@app/booking/stores/scheduling";
import * as Events from "@app/booking/tracking/events";
import type { PublicRssLink } from "@app/booking/types/rssLinkTypes";
import type { ScheduleRecommendation } from "@app/booking/types/scheduleRecommendationTypes";

import NoTimesAvailable from "./NoTimesAvailable";
import { TimeSlotHeader, TimeSlotList, TimeSlotPanel } from "./styles";

const BREAKPOINT = 1200;

const splitByMeridiem = (slots: ScheduleRecommendation[]) =>
  partition((rec) => rec.time.toFormat("a") === "AM", slots);
const isSelectedMonthOutsideConstraints = (
  constraints: PublicRssLink | null,
  selectedMonth: DateTime,
) => {
  if (!constraints) return false;
  return (
    constraints.windowStartTime.toUTC().startOf("month") >
    selectedMonth.toUTC().startOf("month")
  );
};

const SidePanelContent = ({
  isLoading,
  isTodaySelected,
  minimumSchedulingBuffer,
  onChangeTime,
  selectedTime,
  slots,
}: {
  isLoading: boolean;
  isTodaySelected: boolean;
  minimumSchedulingBuffer?: number;
  onChangeTime: (time: DateTime) => void;
  selectedTime: DateTime | null;
  slots: ScheduleRecommendation[];
}) => {
  if (isLoading) {
    return (
      <Stack variant="center" flex={1} sx={{ minHeight: "300px" }}>
        <LoadSpinner />
      </Stack>
    );
  }

  if (isEmpty(slots)) {
    return <NoTimesAvailable />;
  }

  const [morningTimes, afternoonTimes] = splitByMeridiem(slots);

  const showBufferBanner =
    isTodaySelected && (minimumSchedulingBuffer ?? 0) > 0;

  return (
    <Stack alignItems="center" spacing={3} paddingBottom={3}>
      <Row variant="center" spacing={1}>
        <IndicatorLegend />
        <Typography variant="labelReg" color="text.med">
          <Trans>Recommended time</Trans>
        </Typography>
        <TooltipIcon
          title={t`Read recommends meeting times based on promoting factors like engagement, punctuality, and focus time.`}
        />
      </Row>
      {showBufferBanner && (
        <Stack paddingX={1}>
          <InfoPanel severity="info">
            <Trans>
              Scheduling within{" "}
              <Plural
                value={minimumSchedulingBuffer ?? 0}
                one="# hour"
                other="# hours"
              />{" "}
              of the current time is not permitted, therefore those times are
              not shown.
            </Trans>
          </InfoPanel>
        </Stack>
      )}
      <Stack spacing={1}>
        {!isEmpty(morningTimes) && (
          <Stack spacing={0.5}>
            <TimeSlotHeader>
              <Trans>AM</Trans>
            </TimeSlotHeader>
            <TimeSlotList spacing={1}>
              {morningTimes.map(({ isRecommended, time }) => (
                <TimeSlot
                  key={time.toString()}
                  recommended={isRecommended}
                  onClick={onChangeTime}
                  time={time}
                  selected={selectedTime != null && time.equals(selectedTime)}
                />
              ))}
            </TimeSlotList>
          </Stack>
        )}
        {!isEmpty(afternoonTimes) && (
          <Stack spacing={0.5}>
            <TimeSlotHeader>
              <Trans>PM</Trans>
            </TimeSlotHeader>
            <TimeSlotList>
              {afternoonTimes.map(({ isRecommended, time }) => (
                <TimeSlot
                  key={time.toString()}
                  recommended={isRecommended}
                  onClick={onChangeTime}
                  time={time}
                  selected={selectedTime != null && time.equals(selectedTime)}
                />
              ))}
            </TimeSlotList>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

const DateAndTime = ({ onSelectTime }: { onSelectTime?: () => void }) => {
  const {
    availability,
    isAvailabilityLoading,
    isAvailabilitySuccess,
    isTimeSlotsForDayLoading,
    isTimeSlotsForDaySuccess,
    timeSlotsForDay,
  } = useAvailability();

  const sidePanelContentRef = useRef<HTMLInputElement | null>(null);
  const [sidePanelRef] = useAutoAnimate();
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const { windowWidth } = useWindowDimensions();

  const {
    constraints,
    selectedDate,
    selectedMonth,
    onChangeDate,
    onChangeMonth,
    selectedTime,
    onChangeTime,
    selectedTimezone,
    onChangeTimezone,
  } = useSchedulingStore((state) => ({
    constraints: state.constraints,
    selectedDate: state.selectedDate,
    selectedMonth: state.selectedMonth,
    onChangeDate: state.setSelectedDate,
    onChangeMonth: state.setSelectedMonth,
    selectedTime: state.selectedTime,
    onChangeTime: state.setSelectedTime,
    selectedTimezone: state.selectedTimezone,
    onChangeTimezone: state.setSelectedTimezone,
  }));

  const shouldForceSidePanelOpen =
    !sidePanelOpen && isAvailabilitySuccess && isEmpty(availability);

  useEffect(() => {
    if (!shouldForceSidePanelOpen) return;
    setSidePanelOpen(true);
  }, [shouldForceSidePanelOpen]);

  const shouldScrollToTimeSlots =
    windowWidth < BREAKPOINT && isTimeSlotsForDaySuccess;

  useEffect(() => {
    if (shouldScrollToTimeSlots) {
      // scroll down to the time slots when the data loads (will be a noop scroll if
      // in horizontal view)
      sidePanelContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [shouldScrollToTimeSlots]);

  const shouldUpdateSelectedMonth = isSelectedMonthOutsideConstraints(
    constraints,
    selectedMonth,
  );

  useEffect(() => {
    if (!constraints || !shouldUpdateSelectedMonth) return;
    // use UTC because the constraints<>selectedMonth comparison checks against
    // UTC, and so if we don't use UTC, we could get caught in a render loop
    onChangeMonth(constraints.windowStartTime.toUTC());
  }, [shouldUpdateSelectedMonth, onChangeMonth]);

  const shouldDisableDate = (date: DateTime | null) => {
    // don't disable any dates while the calendar is loading
    if (!date || isAvailabilityLoading) return false;

    const formattedDay = date.toFormat("yyyy-MM-dd");
    const dateHasAvailableTimes =
      (availability[formattedDay]?.availabilityScore ?? 0) > 0;
    return !dateHasAvailableTimes;
  };

  const handleSelectDate = (date: DateTime | null) => {
    if (date) {
      Events.selectDate({ date, kind: "default" });
    }
    setSidePanelOpen(true);
    onChangeDate(date);
  };

  const handleSelectTime = (time: DateTime) => {
    onChangeTime(time);
    onSelectTime?.();
  };

  const isTodaySelected =
    (selectedTimezone
      ? DateTime.now().setZone(selectedTimezone)
      : DateTime.now()
    ).toFormat("yyyy-MM-dd") === selectedDate?.toFormat("yyyy-MM-dd");

  const sidePanelIsOpen = sidePanelOpen || !isEmpty(timeSlotsForDay);

  return (
    <Stack
      direction={{ lg: "row" }}
      sx={{ width: "100%", height: "100%" }}
      flexGrow={1}
    >
      <Stack
        alignItems="center"
        sx={{
          px: { xxs: sidePanelOpen ? 1 : 2, lg: sidePanelIsOpen ? 1.5 : 3 },
          pb: 3,
          flexBasis: sidePanelIsOpen ? "50%" : "100%",
          transition: "flex-basis 200ms ease",
        }}
      >
        <Stack maxWidth="400px" spacing={3}>
          {/* force a re-render of the calendar when the constraints change in order
           to show the appropriate month, unfortunately the calendar view's month display
            is not updated with min/max date props change */}
          <CalendarView
            key={
              constraints
                ? "calendarWithConstraints"
                : "calendarWithoutConstraints"
            }
            dayProps={availability}
            value={selectedDate}
            loading={isAvailabilityLoading}
            minDate={constraints?.windowStartTime}
            maxDate={constraints?.windowEndTime}
            onChange={handleSelectDate}
            onMonthChange={onChangeMonth}
            shouldDisableDate={shouldDisableDate}
            referenceDate={selectedMonth}
          />
          <TimezoneSelector
            selected={selectedTimezone}
            onChange={onChangeTimezone}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "outline.default",
              },
            }}
          />
        </Stack>
      </Stack>
      <TimeSlotPanel show={sidePanelIsOpen} spacing={3} ref={sidePanelRef}>
        <Stack ref={sidePanelContentRef} flex={1}>
          {sidePanelIsOpen ? (
            <SidePanelContent
              isLoading={
                isTimeSlotsForDayLoading ||
                (sidePanelOpen && isAvailabilityLoading)
              }
              isTodaySelected={isTodaySelected}
              minimumSchedulingBuffer={constraints?.minimumSchedulingBuffer}
              onChangeTime={handleSelectTime}
              selectedTime={selectedTime}
              slots={timeSlotsForDay}
            />
          ) : null}
        </Stack>
      </TimeSlotPanel>
    </Stack>
  );
};

export default DateAndTime;
